import React from 'react';
import { SmartphoneIcon } from 'lucide-react';
import { useLaunch } from '../contexts/LaunchContext';
import { STORE_BADGE_ASSETS } from '../data/launch';
type Tone = 'light' | 'dark';

/**
 * App Store / Google Play download links.
 *
 * Apple and Google both require their OFFICIAL badge artwork, used to their
 * brand guidelines. Home-made lookalikes are a guideline breach and can hold up
 * a store review, so this component will only render a badge image once the
 * official asset is set in STORE_BADGE_ASSETS.
 *
 * Until then it renders a plain, clearly non-official text button, so nothing
 * ships that pretends to be an Apple or Google badge.
 */
function StoreLink({
  href,
  badgeSrc,
  badgeAlt,
  fallbackLabel,
  tone
}: {
  href: string;
  badgeSrc: string;
  badgeAlt: string;
  fallbackLabel: string;
  tone: Tone;
}) {
  // Official artwork available — render it as the store guidelines require.
  if (badgeSrc && href) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className="inline-block rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow focus-visible:ring-offset-2" data-id="element-265" data-mp="37oaztcala3za">
        <img src={badgeSrc} alt={badgeAlt} className="h-[56px] w-auto" data-id="element-266" data-mp="33d3348juf5v1" />
      </a>;
  }
  const base = 'inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 min-h-[56px] text-base font-bold transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow focus-visible:ring-offset-2';
  const skin = tone === 'dark' ? 'bg-white text-kitty-dark hover:bg-gray-100 focus-visible:ring-offset-gray-950' : 'bg-kitty-dark text-white hover:bg-black focus-visible:ring-offset-white';
  const content = <>
      <SmartphoneIcon className="w-5 h-5 flex-shrink-0" aria-hidden="true" data-id="element-267" data-mp="2aoxh7ih1zatm" />
      {fallbackLabel}
    </>;

  // No store URL configured yet — non-interactive, so there is no dead link.
  if (!href) {
    return <span className={`${base} ${skin} opacity-60 cursor-not-allowed`} aria-disabled="true" data-id="element-268" data-mp="xpvmf7m80wgk">
        {content}
      </span>;
  }
  return <a href={href} target="_blank" rel="noopener noreferrer" className={`${base} ${skin}`} data-id="element-269" data-mp="11pny3t6wviq5">
      {content}
    </a>;
}
export function StoreBadges({
  tone = 'light'
}: {
  tone?: Tone;
}) {
  const {
    appStoreUrl,
    googlePlayUrl
  } = useLaunch();
  return <div className="flex flex-col sm:flex-row gap-3" data-id="element-270" data-mp="e14lj1gtwjs9">
      <StoreLink href={appStoreUrl} badgeSrc={STORE_BADGE_ASSETS.appStoreBadgeSrc} badgeAlt="Download KittyPot on the App Store" fallbackLabel="Download for iPhone" tone={tone} data-id="element-271" data-mp="ite8q9qk75qu" />
      <StoreLink href={googlePlayUrl} badgeSrc={STORE_BADGE_ASSETS.googlePlayBadgeSrc} badgeAlt="Get KittyPot on Google Play" fallbackLabel="Download for Android" tone={tone} data-id="element-272" data-mp="a1ccknniwbmd" />
    </div>;
}